import React, { useState } from "react";
import PageShell from "../components/PageShell";
import TeamAnalysisForm from "../components/TeamAnalysisForm";
import { ArrowRight, Check, Compass, Users, Building2 } from "lucide-react";

const PACKAGES = [
  { icon: Compass, name: "Leadership Essentials", forWho: "For business owners and managers who want to become better leaders.",
    desc: "Help your leadership team learn how to lead with confidence, communicate clearly, and create a workplace built on trust and accountability.",
    items: ["Leadership and management assessment.", "Identifying leadership and communication challenges.", "Guidance on leading employees effectively.", "Setting clear expectations and goals.", "Practical recommendations for improving management."] },
  { icon: Users, name: "Team Growth & Workplace Culture", forWho: "For businesses that want a more motivated, connected, and engaged team.",
    desc: "Help create a workplace where employees feel valued, understand their roles, and work together toward common goals.",
    items: ["Team communication assessment.", "Employee engagement and workplace culture review.", "Identifying opportunities to improve teamwork.", "Recommendations for employee recognition and motivation.", "Ideas for building stronger team relationships.", "Guidance on creating a more positive workplace environment."] },
  { icon: Building2, name: "Business & Team Transformation", forWho: "For businesses ready to improve their leadership, team culture, and overall operations.",
    desc: "A broader approach to understanding how your people, leadership, communication, and company identity work together to support business growth.",
    items: ["Leadership and team assessment.", "Workplace culture and communication review.", "Identification of organizational challenges and opportunities.", "Recommendations for improving teamwork and accountability.", "Guidance on developing company values and a shared vision.", "Team branding recommendations, including custom uniforms, polo shirts, and branded apparel where appropriate.", "A tailored action plan based on the company's needs."] },
];

const STEPS = [
  { title: "Tell Us About Your Business", desc: "Complete the free analysis request form and share your goals and challenges." },
  { title: "Let's Meet", desc: "We'll arrange a suitable time to discuss your business, virtually or at your location when appropriate." },
  { title: "Get Your Recommendations", desc: "You'll receive recommendations for the services and package that best fit your business needs." },
];

export default function GrowYourTeam() {
  const [pkg, setPkg] = useState(null);
  const openForm = (name) => {
    setPkg(name);
    document.getElementById("analysis-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <PageShell kicker="Grow Your Team" title="Build a Stronger Team. Build a Stronger Business." subtitle="Your business is only as strong as the people who help you build it.">
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-4 pb-8 text-center">
        <button onClick={() => openForm(null)} className="btn-peach" data-testid="gt-hero-cta">Get a Free Business Analysis <ArrowRight className="w-4 h-4" /></button>
      </section>

      <section className="max-w-6xl mx-auto px-5 lg:px-8 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div className="rounded-3xl overflow-hidden bg-[color:var(--dms-navy)]">
          <img src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=1200&q=80" alt="Business owner leading a team meeting" className="w-full h-[320px] md:h-[440px] object-cover opacity-90" />
        </div>
        <div className="space-y-4 text-[color:var(--dms-navy)]/75 leading-relaxed" data-testid="gt-intro">
          <p>Marketing can attract customers, but strong leadership, good communication, and a positive workplace culture help your business grow from the inside out.</p>
          <p>We help business owners identify opportunities to improve their leadership, strengthen their teams, and create a workplace where employees feel respected, motivated, and connected to the company's vision.</p>
          <p>Our goal is to help you build a stronger business by investing in the people behind it.</p>
          <p className="font-serif italic text-xl text-[color:var(--dms-navy)] pt-2 border-l-2 border-[color:var(--dms-peach)] pl-4" data-testid="gt-message">When you invest in your people, you invest in the future of your business.</p>
        </div>
      </section>

      <section id="team-packages" className="bg-[color:var(--dms-cream-2)]/50 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center">
            <p className="kicker">Packages</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">Ways we can help your team grow</h2>
            <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
            <p className="mt-6 text-[color:var(--dms-navy)]/70 max-w-2xl mx-auto">Every package is customized. Recommendations and service details are shaped around your business following the free business analysis.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PACKAGES.map((p, i) => <PackageCard key={p.name} pkg={p} index={i} onSelect={() => openForm(p.name)} />)}
          </div>
        </div>
      </section>

      <section className="bg-[color:var(--dms-navy)] py-20" data-testid="gt-free-analysis-section">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <p className="kicker text-[color:var(--dms-peach)]">Free business analysis</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mt-4">Not Sure What Your Business Needs? Let's Find Out.</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          <div className="mt-6 space-y-4 text-white/80">
            <p>Every business is different. That's why we start by understanding your business before recommending a solution.</p>
            <p>Book your FREE business analysis, and let's discuss your current challenges, your team, your goals, and the opportunities available to help your business grow.</p>
            <p>Where appropriate, I can arrange to visit your business in person, learn more about your operations, and provide recommendations based on your needs.</p>
            <p className="font-semibold text-white">There is no obligation to purchase a package.</p>
          </div>
          <button onClick={() => openForm(null)} className="btn-peach mt-8" data-testid="gt-main-cta">GET YOUR FREE BUSINESS ANALYSIS <ArrowRight className="w-4 h-4" /></button>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-20">
        <div className="text-center">
          <p className="kicker">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">How it works</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <div key={s.title} className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-6" data-testid={`gt-step-${i + 1}`}>
              <div className="w-10 h-10 rounded-full bg-[color:var(--dms-peach)] flex items-center justify-center text-[color:var(--dms-navy)] font-serif text-lg font-semibold">{i + 1}</div>
              <p className="kicker mt-4 text-[10px]">Step {i + 1}</p>
              <h3 className="font-serif text-xl text-[color:var(--dms-navy)] mt-1">{s.title}</h3>
              <p className="mt-2 text-sm text-[color:var(--dms-navy)]/65">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="analysis-form" className="scroll-mt-28 bg-[color:var(--dms-cream)] py-20">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <TeamAnalysisForm packageInterest={pkg} onClearPackage={() => setPkg(null)} />
        </div>
      </section>
    </PageShell>
  );
}

function PackageCard({ pkg, index, onSelect }) {
  const Icon = pkg.icon;
  return (
    <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-8 flex flex-col hover-lift" data-testid={`gt-package-${index}`}>
      <div className="w-12 h-12 rounded-full bg-[color:var(--dms-peach-light)]/60 flex items-center justify-center"><Icon className="w-5 h-5 text-[color:var(--dms-navy)]" /></div>
      <p className="kicker mt-5 text-[10px]">Package {index + 1}</p>
      <h3 className="font-serif text-2xl text-[color:var(--dms-navy)] mt-1">{pkg.name}</h3>
      <p className="mt-3 text-sm font-medium text-[color:var(--dms-navy)]/85">{pkg.forWho}</p>
      <p className="mt-3 text-sm text-[color:var(--dms-navy)]/70">{pkg.desc}</p>
      <p className="mt-6 text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">Includes</p>
      <ul className="mt-3 space-y-2.5 flex-1">
        {pkg.items.map((it) => (
          <li key={it} className="flex items-start gap-2.5 text-sm text-[color:var(--dms-navy)]/80">
            <span className="w-5 h-5 rounded-full bg-[color:var(--dms-peach-light)]/70 flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3 h-3 text-[color:var(--dms-navy)]" /></span>{it}
          </li>
        ))}
      </ul>
      <button onClick={onSelect} className="btn-peach mt-8 justify-center" data-testid={`gt-package-${index}-cta`}>Get a Free Business Analysis <ArrowRight className="w-4 h-4" /></button>
    </div>
  );
}
